import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Button, 
  Breadcrumbs,
  Link as MuiLink,
  Tabs,
  Tab,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Rating,
  Chip,
  Avatar,
  Badge,
  IconButton,
  Tooltip,
  LinearProgress,
  Fade,
  TextField
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Store as StoreIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as WebsiteIcon,
  ShoppingBag as ShoppingBagIcon,
  Star as StarIcon,
  Verified as VerifiedIcon,
  AccessTime as TimeIcon,
  ShoppingCart as CartIcon,
  Favorite as FavoriteIcon,
  Share as ShareIcon,
  Home as HomeIcon,
  LocalShipping as ShippingIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import storeDetailBn from '../i18n/bn/storeDetail';
import storeDetailEn from '../i18n/en/storeDetail';
import storeDetailNp from '../i18n/np/storeDetail';
import storeDetailPk from '../i18n/pk/storeDetail';
import storeDetailBt from '../i18n/bt/storeDetail';
import storeDetailDe from '../i18n/de/storeDetail';
import storeDetailSl from '../i18n/sl/storeDetail';
import storeDetailAf from '../i18n/af/storeDetail';
import storeDetailCn from '../i18n/cn/storeDetail';
import { convertEnglishToBengali } from '../utils/priceUtils';

// Styled Components
const StoreHeaderBox = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url(data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E)',
    animation: 'float 20s ease-in-out infinite',
  },
  '@keyframes float': {
    '0%, 100%': {
      transform: 'translateY(0px)',
    },
    '50%': {
      transform: 'translateY(-20px)',
    },
  },
}));

const StoreAvatar = styled(Avatar)(({ theme }) => ({
  width: 140,
  height: 140,
  border: '6px solid white',
  boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
  }
}));

const ProductCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '5px',
  overflow: 'hidden',
  border: '1px solid rgba(0,0,0,0.05)',
  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    '& .MuiCardMedia-root': {
      transform: 'scale(1.1)',
    }
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '5px',
  padding: theme.spacing(1, 2.5),
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
  },
  '&.MuiButton-contained': {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    '&:hover': {
      background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
    }
  }
}));

const InfoCard = styled(Paper)(({ theme }) => ({
  borderRadius: '5px',
  padding: theme.spacing(3),
  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
  boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
  border: '1px solid rgba(0,0,0,0.05)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 15px 50px rgba(0,0,0,0.12)',
  }
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: '5px',
  fontWeight: 600,
  background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
  color: 'white',
  '&:hover': {
    background: 'linear-gradient(45deg, #5a67d8 30%, #6b46c1 90%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
  }
}));

const StoreDetail = () => {
  const { slug } = useParams();
  const [tabValue, setTabValue] = useState(0);
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('সব'); // Filter state
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);

  const lang = (typeof window !== 'undefined' && localStorage.getItem('preferredLanguage')) || 'bn';
  const t = lang === 'en' ? storeDetailEn : lang === 'np' ? storeDetailNp : lang === 'pk' ? storeDetailPk : lang === 'bt' ? storeDetailBt : lang === 'de' ? storeDetailDe : lang === 'sl' ? storeDetailSl : lang === 'af' ? storeDetailAf : lang === 'cn' ? storeDetailCn : storeDetailBn;

  // Mock data for store details based on slug
  useEffect(() => {
    // In a real app, this would be an API call
    setTimeout(() => {
      const storeData = {
        'dayaal-supershop': {
          id: 1,
          name: 'দয়াল সুপারশপ',
          slug: 'dayaal-supershop',
          description: 'আমরা একটি প্রিমিয়াম দোকান যা প্রতিযোগিতামূলক মূল্যে উচ্চ মানের পণ্য সরবরাহ করে। আমরা চমৎকার গ্রাহক সেবা এবং আপনার সমস্ত প্রয়োজন মেটানোর জন্য বিস্তৃত পণ্যের পরিসর নিয়ে গর্বিত।',
          logo: `https://picsum.photos/200/200?random=1`,
          banner: `https://picsum.photos/1200/400?random=1`,
          address: '৪৫ মার্কেট স্ট্রিট, চাঁচকৈর বাজার, ঢাকা, বাংলাদেশ',
          phone: '+৮৮০ ১২৩৪ ৫৬৭৮৯০',
          email: 'info@dayaalsupershop.com',
          website: 'www.dayaalsupershop.com',
          rating: 4.7,
          reviewCount: 156,
          isVerified: true,
          totalProducts: 45,
          bazar: {
            id: 1,
            name: 'চাঁচকৈর বাজার',
            slug: 'chanchkoir-bazar'
          },
          categories: ['শাকসবজি', 'ফলমূল', 'জৈব খাদ্য', 'মাছ মাংস'],
          openingHours: {
            weekdays: 'সকাল ৮:০০ - রাত ৮:০০',
            saturday: 'সকাল ৯:০০ - সন্ধ্যা ৭:০০',
            sunday: 'সকাল ১০:০০ - সন্ধ্যা ৬:০০'
          }
        }
      };
      
      setStore(storeData[slug] || storeData['dayaal-supershop']);

      setProducts([
        { id: 1, name: 'তাজা টমেটো', image: `https://picsum.photos/300/200?random=11`, price: 120, originalPrice: 140, unit: 'কেজি', rating: 4.8, stock: 25, category: 'শাকসবজি' },
        { id: 2, name: 'জৈব আলু', image: `https://picsum.photos/300/200?random=12`, price: 80, originalPrice: 95, unit: 'কেজি', rating: 4.6, stock: 40, category: 'জৈব খাদ্য' },
        { id: 3, name: 'তাজা পালংশাক', image: `https://picsum.photos/300/200?random=13`, price: 60, originalPrice: 70, unit: 'বান্ডিল', rating: 4.7, stock: 15, category: 'শাকসবজি' },
        { id: 4, name: 'লাল পেঁয়াজ', image: `https://picsum.photos/300/200?random=14`, price: 90, originalPrice: 100, unit: 'কেজি', rating: 4.5, stock: 30, category: 'শাকসবজি' },
        { id: 5, name: 'সবুজ মরিচ', image: `https://picsum.photos/300/200?random=15`, price: 150, originalPrice: 170, unit: 'কেজি', rating: 4.4, stock: 20, category: 'শাকসবজি' },
        { id: 6, name: 'তাজা গাজর', image: `https://picsum.photos/300/200?random=16`, price: 70, originalPrice: 85, unit: 'কেজি', rating: 4.9, stock: 35, category: 'শাকসবজি' },
        { id: 7, name: 'তাজা আপেল', image: `https://picsum.photos/300/200?random=17`, price: 180, originalPrice: 200, unit: 'কেজি', rating: 4.6, stock: 20, category: 'ফলমূল' },
        { id: 8, name: 'পাকা আম', image: `https://picsum.photos/300/200?random=18`, price: 150, originalPrice: 170, unit: 'কেজি', rating: 4.8, stock: 15, category: 'ফলমূল' },
        { id: 9, name: 'তাজা মাছ', image: `https://picsum.photos/300/200?random=19`, price: 300, originalPrice: 350, unit: 'কেজি', rating: 4.7, stock: 10, category: 'মাছ মাংস' },
        { id: 10, name: 'খাসির মাংস', image: `https://picsum.photos/300/200?random=20`, price: 800, originalPrice: 900, unit: 'কেজি', rating: 4.9, stock: 8, category: 'মাছ মাংস' },
        { id: 11, name: 'জৈব চাল', image: `https://picsum.photos/300/200?random=21`, price: 90, originalPrice: 100, unit: 'কেজি', rating: 4.5, stock: 50, category: 'জৈব খাদ্য' },
        { id: 12, name: 'জৈব ডাল', image: `https://picsum.photos/300/200?random=22`, price: 110, originalPrice: 130, unit: 'কেজি', rating: 4.4, stock: 30, category: 'জৈব খাদ্য' },
      ]);
      
      setLoading(false);
    }, 500);
  }, [slug]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
  };

  // Filter products based on selected category
  const filteredProducts = selectedCategory === 'সব' 
    ? products 
    : products.filter(product => product.category === selectedCategory);

  // Get product count for each category
  const getCategoryCount = (category) => {
    if (category === 'সব') return products.length;
    return products.filter(product => product.category === category).length;
  };

  const handleWriteReviewClick = () => {
    setShowReviewForm(!showReviewForm);
    if (!showReviewForm) {
      setReviewRating(5); // Reset rating when opening form
    }
  };

  const handleReviewSubmit = (reviewData) => {
    console.log('Review submitted:', { ...reviewData, rating: reviewRating });
    // Show success message (you can add a snackbar here)
    alert(`আপনার ${convertEnglishToBengali(reviewRating.toString())} স্টার রিভিউ সফলভাবে জমা দেওয়া হয়েছে!`);
    setShowReviewForm(false);
    setReviewRating(5); // Reset rating after submit
  };

  if (loading) {
    return (
      <>
        <Header />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 8, minHeight: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography>{t.loading}</Typography>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <StoreHeaderBox
        sx={{ 
          backgroundImage: `linear-gradient(90deg, rgba(2,0,36,1) 0%, rgba(9,9,121,0.6979166666666667) 10%, rgba(141,0,255,0.44861694677871145) 100%), url(${store.banner})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          mb: 6,
          position: 'relative'
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: { xs: 'column', md: 'row' }, gap: '30px' }}>
            <StoreAvatar 
              src={store.logo} 
              alt={store.name}
            />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <Typography variant="h2" component="h1" sx={{ fontWeight: 800, mr: 2 }}>
                    {store.name}
                  </Typography>
                  {store.isVerified && (
                    <Tooltip title={t.verified}>
                      <VerifiedIcon sx={{ fontSize: 40, color: '#2196f3' }} />
                    </Tooltip>
                  )}
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <Rating value={store.rating} precision={0.1} readOnly size="large" />
                  <Typography variant="h5" sx={{ ml: 2, fontWeight: 600 }}>
                    {convertEnglishToBengali(store.rating.toString())}
                  </Typography>
                  <Typography variant="h6" sx={{ ml: 1, opacity: 0.9 }}>
                    ({convertEnglishToBengali(store.reviewCount.toString())} রিভিউ)
                  </Typography>
                </Box>
                
                <Typography variant="h5" sx={{ mb: 2, opacity: 0.95 }}>
                  {t.locatedIn(store.bazar.name)}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <Chip 
                    icon={<ShoppingBagIcon />}
                    label={`${convertEnglishToBengali(store.totalProducts.toString())} ${t.labels.products}`}
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '1rem',
                      backdropFilter: 'blur(10px)'
                    }}
                  />
                  <Chip 
                    icon={<StarIcon />}
                    label={t.labels.premiumStore}
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '1rem',
                      backdropFilter: 'blur(10px)'
                    }}
                  />
                                 </Box>
               </Box>
             </Box>
           </Box>
         </Container>
      </StoreHeaderBox>

      <Container maxWidth="xl" sx={{ mb: 8 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 4 }}>
          <MuiLink 
            component={Link} 
            to="/" 
            color="inherit"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            {t.breadcrumbs.home}
          </MuiLink>
          <MuiLink component={Link} to="/bazars" color="inherit" sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            {t.breadcrumbs.bazars}
          </MuiLink>
          <MuiLink component={Link} to={`/bazars/${store.bazar.slug}`} color="inherit" sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            {store.bazar.name}
          </MuiLink>
          <Typography color="text.primary" sx={{ fontWeight: 600 }}>{store.name}</Typography>
        </Breadcrumbs>

        <Grid container spacing={6}>
          <Grid item xs={12} md={8}>
            <InfoCard sx={{ mb: 4, overflow: 'hidden' }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                variant="fullWidth"
                textColor="primary"
                indicatorColor="primary"
                sx={{
                  bgcolor: 'grey.50',
                  '& .MuiTab-root': {
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    textTransform: 'none',
                    py: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: 'primary.50',
                      color: 'primary.main'
                    },
                    '&.Mui-selected': {
                      bgcolor: 'white',
                      color: 'primary.main',
                      fontWeight: 700
                    }
                  },
                  '& .MuiTabs-indicator': {
                    height: 4,
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)'
                  }
                }}
              >
                <Tab label={t.tabs.products} />
                <Tab label={t.tabs.about} />
                <Tab label={t.tabs.reviews} />
              </Tabs>
              <Box sx={{ p: 3 }}>
                {tabValue === 0 && (
                  <Fade in={tabValue === 0} timeout={500}>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {t.productsSection.titleWithCount(convertEnglishToBengali(filteredProducts.length.toString()))}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <StyledChip 
                          label={`${t.common.all} (${convertEnglishToBengali(getCategoryCount('সব').toString())})`}
                          size="small"
                          onClick={() => handleCategoryFilter('সব')}
                          sx={{ 
                            cursor: 'pointer',
                            background: selectedCategory === 'সব' 
                              ? 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)' 
                              : 'linear-gradient(45deg, #e0e0e0 30%, #bdbdbd 90%)',
                            color: selectedCategory === 'সব' ? 'white' : 'black',
                            '&:hover': {
                              background: selectedCategory === 'সব' 
                                ? 'linear-gradient(45deg, #5a67d8 30%, #6b46c1 90%)' 
                                : 'linear-gradient(45deg, #d5d5d5 30%, #a8a8a8 90%)',
                            }
                          }}
                        />
                        {store.categories.map((category, index) => (
                          <StyledChip 
                            key={index} 
                            label={`${category} (${convertEnglishToBengali(getCategoryCount(category).toString())})`}
                            size="small"
                            onClick={() => handleCategoryFilter(category)}
                            sx={{ 
                              cursor: 'pointer',
                              background: selectedCategory === category 
                                ? 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)' 
                                : 'linear-gradient(45deg, #e0e0e0 30%, #bdbdbd 90%)',
                              color: selectedCategory === category ? 'white' : 'black',
                              '&:hover': {
                                background: selectedCategory === category 
                                  ? 'linear-gradient(45deg, #5a67d8 30%, #6b46c1 90%)' 
                                  : 'linear-gradient(45deg, #d5d5d5 30%, #a8a8a8 90%)',
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                    <Grid container spacing={4}>
                      {filteredProducts.map((product) => (
                        <Grid item xs={12} sm={6} lg={4} key={product.id}>
                          <ProductCard>
                            <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                              <CardMedia
                                component="img"
                                height="200"
                                image={product.image}
                                alt={product.name}
                                sx={{ transition: 'transform 0.3s ease' }}
                              />
                              {product.originalPrice > product.price && (
                                <Chip
                                  label={`${convertEnglishToBengali(Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100).toString())}% ${t.labels.discount}`}
                                  color="error"
                                  sx={{
                                    position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    bgcolor: '#ff4444',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.75rem'
                                  }}
                                />
                              )}
                              <Box sx={{
                                position: 'absolute',
                                top: 12,
                                left: 12,
                                display: 'flex',
                                gap: 1
                              }}>
                                <Tooltip title={t.tooltips.addToWishlist}>
                                  <IconButton size="small" sx={{ bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'white', color: 'red' } }}>
                                    <FavoriteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                            <CardContent sx={{ flexGrow: 1, p: 3 }}>
                              <Typography gutterBottom variant="h6" component="h3" sx={{ fontWeight: 600, mb: 2 }}>
                                {product.name}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Rating value={product.rating} size="small" precision={0.1} readOnly />
                                <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                                  ({convertEnglishToBengali(product.rating.toString())})
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                                  ৳{convertEnglishToBengali(product.price.toString())}
                                </Typography>
                                {product.originalPrice > product.price && (
                                  <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                    ৳{convertEnglishToBengali(product.originalPrice.toString())}
                                  </Typography>
                                )}
                                <Typography variant="body2" color="text.secondary">
                                  /{product.unit}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {t.labels.stock}
                                </Typography>
                                <Chip 
                                  label={`${convertEnglishToBengali(product.stock.toString())} ${product.unit}`}
                                  size="small"
                                  color={product.stock > 10 ? "success" : "warning"}
                                  variant="outlined"
                                />
                              </Box>
                            </CardContent>
                            <Box sx={{ p: 3, pt: 0, display: 'flex', gap: 2 }}>
                              <ActionButton 
                                size="small" 
                                variant="outlined"
                                component={Link} 
                                to={`/daily-bazar/${product.id}`}
                                sx={{ flex: 1 }}
                               >
                                {t.buttons.details}
                              </ActionButton>
                              <ActionButton 
                                size="small" 
                                variant="contained" 
                                startIcon={<CartIcon />}
                                sx={{ 
                                  flex: 1,
                                  whiteSpace: 'nowrap',
                                  minWidth: 'auto'
                                }}
                               >
                                {t.buttons.addToCart}
                              </ActionButton>
                            </Box>
                          </ProductCard>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                  </Fade>
                )}
                {tabValue === 1 && (
                  <Fade in={tabValue === 1} timeout={500}>
                  <Box>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: 'primary.main', mb: 3 }}>
                      {t.about.title(store.name)}
                    </Typography>
            <Paper sx={{ p: 3, bgcolor: 'grey.50', borderRadius: '5px', mb: 4 }}>
                      <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.primary' }}>
                        {store.description}
                      </Typography>
                    </Paper>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ 
                          p: 3, 
                          bgcolor: 'success.50', 
                          borderRadius: '5px', 
                          border: '1px solid', 
                          borderColor: 'success.200',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'success.dark' }}>
                            {t.about.productTypes}
                          </Typography>
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 1, 
                            flexWrap: 'wrap', 
                            mt: 2,
                            flex: 1,
                            alignContent: 'flex-start'
                          }}>
                            {store.categories.map((category, index) => (
                              <StyledChip 
                                key={index} 
                                label={category} 
                                size="small"
                              />
                            ))}
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ 
                          p: 3, 
                          bgcolor: 'info.50', 
                          borderRadius: '5px', 
                          border: '1px solid', 
                          borderColor: 'info.200',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'info.dark' }}>
                            {t.about.stats}
                          </Typography>
                          <Box sx={{ 
                            mt: 2,
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start'
                          }}>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              <strong>{t.labels.totalProducts}:</strong> {convertEnglishToBengali(store.totalProducts.toString())}টি
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              <strong>{t.labels.rating}:</strong> {convertEnglishToBengali(store.rating.toString())} {t.labels.stars}
                            </Typography>
                            <Typography variant="body1">
                              <strong>{t.labels.reviews}:</strong> {convertEnglishToBengali(store.reviewCount.toString())}টি
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                  </Fade>
                )}
                {tabValue === 2 && (
                  <Fade in={tabValue === 2} timeout={500}>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {t.review.heading}
                      </Typography>
                      <ActionButton
                        variant="contained"
                        startIcon={<StarIcon />}
                        onClick={handleWriteReviewClick}
                        sx={{
                          whiteSpace: 'nowrap',
                          minWidth: 'auto',
                          px: { xs: 2, sm: 3 },
                          fontSize: { xs: '0.9rem', sm: '1.1rem' },
                          ...(showReviewForm && {
                            background: 'linear-gradient(45deg, #f44336 30%, #d32f2f 90%) !important',
                            '&:hover': {
                              background: 'linear-gradient(45deg, #d32f2f 30%, #c62828 90%) !important',
                            }
                          })
                        }}
                      >
                        {showReviewForm ? t.review.cancel : t.review.write}
                      </ActionButton>
                    </Box>
                    
                    <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50', borderRadius: '5px', border: '1px solid', borderColor: 'primary.200' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                        <Typography variant="h2" sx={{ fontWeight: 800, color: 'primary.main', mr: 2 }}>
                          {convertEnglishToBengali(store.rating.toString())}
                        </Typography>
                        <Box>
                          <Rating value={store.rating} precision={0.1} readOnly size="large" />
                          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                            {t.review.basedOn(convertEnglishToBengali(store.reviewCount.toString()))}
                          </Typography>
                        </Box>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={(store.rating / 5) * 100} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: 4
                          }
                        }} 
                                            />
                    </Paper>

                    {/* Review Form - Between Rating and Reviews */}
                    {showReviewForm && (
                      <Fade in={showReviewForm} timeout={500}>
                        <Paper sx={{ 
                          p: 4, 
                          my: 3, 
                          bgcolor: 'grey.50', 
                          borderRadius: '5px', 
                          border: '2px solid', 
                          borderColor: 'primary.main',
                          boxShadow: '0 8px 32px rgba(33, 150, 243, 0.15)'
                        }}>
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                            {t.review.formTitle}
                          </Typography>
                          
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label={t.review.yourName}
                                variant="outlined"
                                sx={{ mb: 2 }}
                              />
                            </Grid>
                            
                            <Grid item xs={12} sm={6}>
                              <Box>
                                <Typography variant="body2" gutterBottom sx={{ fontWeight: 600 }}>
                                  {t.review.ratingLabel}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                  <Rating
                                    value={reviewRating}
                                    onChange={(event, newValue) => {
                                      setReviewRating(newValue);
                                    }}
                                    precision={0.1}
                                    size="large"
                                    sx={{ 
                                      '& .MuiRating-iconFilled': {
                                        color: '#ff6d75'
                                      },
                                      '& .MuiRating-iconHover': {
                                        color: '#ff3d47'
                                      }
                                    }}
                                  />
                                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', ml: 1 }}>
                                    {convertEnglishToBengali(reviewRating ? reviewRating.toFixed(1) : '0.0')}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                  {t.review.ratingHint}
                                </Typography>
                              </Box>
                            </Grid>
                            
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label={t.review.commentPlaceholder}
                                multiline
                                rows={4}
                                variant="outlined"
                                placeholder={t.review.commentPlaceholder}
                                sx={{ mb: 3 }}
                              />
                            </Grid>
                          </Grid>
                          
                          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                              variant="outlined"
                              onClick={() => setShowReviewForm(false)}
                              sx={{ 
                                borderRadius: 2, 
                                px: { xs: 2, sm: 3 },
                                whiteSpace: 'nowrap',
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                minWidth: 'auto'
                              }}
                            >
                              {t.buttons.cancel}
                            </Button>
                            
                            <ActionButton
                              variant="contained"
                              onClick={() => handleReviewSubmit({})}
                              sx={{
                                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                                whiteSpace: 'nowrap',
                                minWidth: 'auto',
                                px: { xs: 2, sm: 3 },
                                fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                '&:hover': {
                                  background: 'linear-gradient(45deg, #1976D2 30%, #0288D1 90%)',
                                }
                              }}
                            >
                              {t.review.submit}
                            </ActionButton>
                          </Box>
                        </Paper>
                      </Fade>
                    )}
                    
                    <List sx={{ bgcolor: 'white', borderRadius: '5px' }}>
                       {[
                         { name: 'আহমেদ হাসান', rating: 5, comment: 'অসাধারণ দোকান! সব সময় তাজা পণ্য পাই এবং দামও যুক্তিসঙ্গত। কর্মচারীরা খুবই সহায়ক।', time: `${convertEnglishToBengali('1')} দিন আগে` },
                         { name: 'ফাতেমা খাতুন', rating: 4, comment: 'ভালো মানের পণ্য এবং পরিষ্কার-পরিচ্ছন্ন দোকান। কিছু দাম একটু বেশি মনে হয়েছে।', time: `${convertEnglishToBengali('3')} দিন আগে` },
                         { name: 'রফিকুল ইসলাম', rating: 5, comment: 'এই এলাকার সেরা দোকান। গুণগত মান এবং দ্রুত সেবার জন্য ধন্যবাদ!', time: `${convertEnglishToBengali('1')} সপ্তাহ আগে` },
                         { name: 'সালমা আক্তার', rating: 4, comment: 'তাজা শাকসবজি এবং ফলমূলের জন্য এটি আমার প্রিয় দোকান। প্যাকেজিং ভালো।', time: `${convertEnglishToBengali('2')} সপ্তাহ আগে` },
                         { name: 'মোহাম্মদ করিম', rating: 5, comment: 'দারুণ অভিজ্ঞতা! দোকানি ভাই খুব ভদ্র এবং সব পণ্য সম্পর্কে ভালো জানেন।', time: `${convertEnglishToBengali('3')} সপ্তাহ আগে` }
                       ].map((review, index) => (
                         <React.Fragment key={index}>
                           <ListItem sx={{ 
                             py: 3,
                             px: 0,
                             '&:hover': { 
                               bgcolor: 'grey.50',
                               borderRadius: 2,
                               transition: 'all 0.3s ease'
                             }
                           }}>
                             <Avatar sx={{ bgcolor: 'primary.main', mr: 3, width: 56, height: 56 }}>
                               {review.name.charAt(0)}
                             </Avatar>
                             <ListItemText 
                               primary={
                                 <Box>
                                                                       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, pr: 2 }}>
                                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {review.name}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                                        {review.time}
                                      </Typography>
                                    </Box>
                                   <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                     <Rating value={review.rating} size="small" readOnly />
                                     <Typography variant="body2" sx={{ ml: 1, fontWeight: 600, color: 'primary.main' }}>
                                       {convertEnglishToBengali(review.rating.toString())} স্টার
                                     </Typography>
                                   </Box>
                                   <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.primary' }}>
                                     "{review.comment}"
                                   </Typography>
                                 </Box>
                               } 
                             />
                           </ListItem>
                           {index < 4 && <Divider sx={{ my: 1 }} />}
                         </React.Fragment>
                       ))}
                     </List>
                  </Box>
                  </Fade>
                )}
                              </Box>
            </InfoCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <InfoCard sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: 'primary.main', mb: 3 }}>
                {t.contact.title}
              </Typography>
              <List sx={{ p: 0 }}>
                <ListItem sx={{ px: 0, py: 2 }}>
                  <ListItemIcon>
                    <LocationIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t.contact.address} 
                    secondary={store.address}
                    primaryTypographyProps={{ fontWeight: 600, color: 'text.primary' }}
                    secondaryTypographyProps={{ fontSize: '0.95rem', mt: 0.5 }}
                  />
                </ListItem>
                <Divider />
                <ListItem sx={{ px: 0, py: 2 }}>
                  <ListItemIcon>
                    <PhoneIcon sx={{ color: 'success.main', fontSize: 28 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t.contact.phone} 
                    secondary={store.phone}
                    primaryTypographyProps={{ fontWeight: 600, color: 'text.primary' }}
                    secondaryTypographyProps={{ fontSize: '0.95rem', mt: 0.5 }}
                  />
                </ListItem>
                <Divider />
                <ListItem sx={{ px: 0, py: 2 }}>
                  <ListItemIcon>
                    <EmailIcon sx={{ color: 'info.main', fontSize: 28 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t.contact.email} 
                    secondary={store.email}
                    primaryTypographyProps={{ fontWeight: 600, color: 'text.primary' }}
                    secondaryTypographyProps={{ fontSize: '0.95rem', mt: 0.5 }}
                  />
                </ListItem>
                <Divider />
                <ListItem sx={{ px: 0, py: 2 }}>
                  <ListItemIcon>
                    <WebsiteIcon sx={{ color: 'warning.main', fontSize: 28 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t.contact.website} 
                    secondary={
                      <MuiLink 
                        href={`https://${store.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                          fontSize: '0.95rem', 
                          mt: 0.5,
                          color: 'warning.main',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: 'warning.dark'
                          }
                        }}
                      >
                        {store.website}
                      </MuiLink>
                    }
                    primaryTypographyProps={{ fontWeight: 600, color: 'text.primary' }}
                  />
                </ListItem>
              </List>
            </InfoCard>
            
            <InfoCard sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: 'primary.main', mb: 3 }}>
                {t.hours.title}
              </Typography>
              <List dense sx={{ p: 0 }}>
                <ListItem sx={{ px: 0, py: 1.5 }}>
                  <ListItemIcon>
                    <ScheduleIcon sx={{ color: 'primary.main' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t.hours.weekdaysLabel} 
                    secondary={store.openingHours.weekdays}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 1.5 }}>
                  <ListItemIcon>
                    <ScheduleIcon sx={{ color: 'warning.main' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t.hours.saturdayLabel} 
                    secondary={store.openingHours.saturday}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 1.5 }}>
                  <ListItemIcon>
                    <ScheduleIcon sx={{ color: 'error.main' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t.hours.sundayLabel} 
                    secondary={store.openingHours.sunday}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
              <ActionButton 
                variant="contained" 
                fullWidth 
                startIcon={<CartIcon />}
                component={Link}
                to="/cart"
                size="large"
                sx={{ py: 1.5, fontSize: '1.1rem' }}
              >
                {t.buttons.viewCart}
              </ActionButton>
            </InfoCard>

            <InfoCard>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                {t.quickStats.title}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50', borderRadius: '5px' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {convertEnglishToBengali(store.totalProducts.toString())}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t.quickStats.products}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.50', borderRadius: '5px' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                      {convertEnglishToBengali(store.rating.toString())}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t.quickStats.rating}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </InfoCard>
          </Grid>
        </Grid>
      </Container>
      <Footer />
    </>
  );
};

export default StoreDetail;
